import { X } from "lucide-react";
import { useId, useRef } from "react";
import { useDialogoMecanica } from "./dialogoMecanica";

export function Modal({
  open,
  onClose,
  title,
  children,
  maxWidth = "max-w-2xl",
  heightClassName = "",
  bodyClassName = "flex-1 overflow-auto px-4 sm:px-6 py-4 sm:py-5",
  dismissible = false,
  escFecha = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: string;
  /**
   * Altura FIXA do diálogo (ex.: `h-[90vh]`). Vazio = o modal cresce com o
   * conteúdo, que é o certo pra 99% dos diálogos.
   *
   * O Acerto v4 precisa do contrário: ele tem 3 zonas e a do meio rola por
   * dentro. Sem altura fixa, o modal encolhe até a altura da lista de sócios e
   * o dono volta a rolar dentro de uma fresta — o defeito de espaço que o v4
   * veio corrigir. Continua limitado pelo `max-h` de segurança abaixo.
   */
  heightClassName?: string;
  /**
   * Classes do CORPO do modal. O padrão rola o conteúdo inteiro, que é o certo
   * pra 99% dos diálogos. O Acerto v3 precisa do contrário: 3 zonas, e só a do
   * meio rola (a tesouraria em cima e o placar embaixo têm que ficar SEMPRE
   * visíveis — decidir sem ver quanto sobra é decidir no escuro). Aí ele passa
   * `flex-1 min-h-0 overflow-hidden` e controla a rolagem por dentro.
   */
  bodyClassName?: string;
  /**
   * Controla se clicar fora (backdrop) e Esc fecham o modal.
   * Default FALSE (decisão do Ruddy, 2026-06-02): nenhum modal do sistema
   * fecha por clique-fora/Esc — só pelo X ou botão Cancelar. Evita perder
   * dados digitados por engano. Passe dismissible={true} só em casos raros
   * onde fechar clicando fora seja realmente desejado (ex: visualização).
   */
  dismissible?: boolean;
  /**
   * Esc fecha, clique fora NÃO. Exceção específica do Contrato do Operador (880-UX01; pedido
   * 881/883 do dono, 22/10/2026): Escape cancela sem gravar e o foco volta ao acionador. A regra
   * de 02/06/2026 acima continua valendo para todos os outros modais (padrão false).
   */
  escFecha?: boolean;
}) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  /**
   * Foco preso, Esc, rolagem travada e foco restaurado vivem em
   * `useDialogoMecanica` — a MESMA mecânica que a gaveta lateral (`pele/Drawer`)
   * usa. O comentário grande sobre o bug do foco (14/07) mora lá, junto do
   * código que ele explica.
   */
  useDialogoMecanica({ open, onClose, dismissible, dialogRef, escFecha });

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/50"
      onClick={dismissible ? onClose : undefined}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`bg-card border sm:rounded-lg rounded-t-lg shadow-xl w-full ${maxWidth} ${heightClassName} max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b">
          <h2 id={titleId} className="text-base sm:text-lg font-semibold truncate pr-2">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0 p-2 -mr-1 min-w-[40px] min-h-[40px] flex items-center justify-center rounded-md hover:bg-muted"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>
        <div className={bodyClassName}>{children}</div>
      </div>
    </div>
  );
}
